<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <h6>{{ $t('sources.source_files_heading') }}
        <span v-if="planLevel === 'basic'">({{ this.activeSources.length }} / 2)</span>
        <span v-if="planLevel === 'pro'">({{ this.activeSources.length }} / 4)</span>
      </h6>
      <div class="alert alert-danger d-flex align-items-center" v-if="limitExceeded">
        <img class="icon-small mr-4" v-bind:src="'/app/images/icons/warning_danger.svg'" />
        <span class="mr-2">Limit dostępnych źródeł został osiągnięty.</span>
        <router-link :to="{ name: 'settings'}">Zmień plan.</router-link>
      </div>
      <div class="d-flex align-items-center">
        <router-link :to="{ name: 'source.create', params: { projectId: this.projectId }}" class="mr-2" v-bind:class="{ 'disabled-router-link': limitExceeded }">
          <button :disabled="limitExceeded" type="button" class="btn btn-large btn-orange-dark">
            <plus-circle-icon></plus-circle-icon>
            <span>{{ $t('sources.add_new_file') }}</span>
          </button>
        </router-link>
        <img class="tooltip-icon icon-small ml-4" id="tooltip-sources" v-bind:src="'/app/images/icons/info.svg'" />
        <b-popover target="tooltip-sources" variant="warning" triggers="hover" placement="bottom">
          <div v-html="$t('tooltips.sources')"></div>
        </b-popover>
      </div>
    </template>
    <template slot="dashboard-body">
      <div v-if="!isLoading">
        <div v-if="activeSources.length > 0">
          <p class="m-4"><strong>{{ $t('sources.active_base_files') }}</strong></p>
          <div class="dashboard-content" v-for="source in activeSources" v-bind:key="source.id">
            <source-summary
              @onSourcePreview="previewSource"
              @onSourceRemove="deleteSource"
              @onSourceStatusUpdate="updateSourceStatus"
              :alerts="alerts"
              :hasAlerts="hasSourceAlerts(source.id)"
              :projectId="projectId"
              :source="source">
            </source-summary>
          </div>
        </div>
        <div v-if="inactiveSources.length > 0">
          <p class="m-4"><strong>{{ $t('sources.inactive_base_files') }}</strong></p>
          <div class="dashboard-content" v-for="source in inactiveSources" v-bind:key="source.id">
            <source-summary
              @onSourcePreview="previewSource"
              @onSourceRemove="deleteSource"
              @onSourceStatusUpdate="updateSourceStatus"
              :alerts="alerts"
              :hasAlerts="hasSourceAlerts(source.id)"
              :projectId="projectId"
              :source="source">
            </source-summary>
          </div>
        </div>
        <div v-if="sources.length == 0 && !isLoading" class="dashboard-content mt-4">
          <div class="alert alert-danger mb-4 mt-3" role="alert">
            {{ $t('sources.no_files') }}
          </div>
        </div>
      </div>
      <div v-else>
        <div class="spinner-border text-orange" role="status"></div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import axios from 'axios'
import DashboardTemplate from '@/views/Dashboard'
import SourceSummary from './Partials/SourceSummary'
import { PlusCircleIcon } from 'vue-feather-icons'

export default {
  name: 'Sources',

  data () {
    return {
      alerts: [],
      isLoading: true,
      planLevel: this.$attrs.project.planLevel,
      projectId: this.$attrs.project.id,
      sources: []
    }
  },

  created () {
    this.getProjectSources(this.projectId)
  },

  methods: {
    getProjectSources (projectId) {
      this.isLoading = true
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/data/sources/' + projectId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.sources = response.data
            this.getProjectAlerts(projectId)
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    getProjectAlerts (projectId) {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/alerts/' + projectId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.alerts = response.data
            if (response.data.project === undefined) this.alerts.project = []
            if (response.data.source === undefined) this.alerts.source = []
            if (response.data.feed === undefined) this.alerts.feed = []
            this.isLoading = false
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    updateSourceStatus (source) {
      const sourceConfig = {
        name: source.name,
        active: source.active,
        description: '',
        config: source.config
      }

      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/sources/update/' + this.$route.params.id + '/' + source.id, sourceConfig)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            if (source.active) {
              this.$bvToast.toast(this.$t('sources.source_file_activation_description') + '(' + source.name + ').', {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_file_activation'),
                variant: 'success'
              })
            } else {
              this.$bvToast.toast(this.$t('sources.source_file_deactivation_description') + '(' + source.name + ').', {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_file_deactivation'),
                variant: 'success'
              })
            }
          }
        })
        .catch((error) => {
          if (error.response) {
            if (source.active) {
              this.$bvToast.toast(this.$t('sources.source_file_activation_error_description') + '(' + source.name + ').', {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_file_activation_error'),
                variant: 'danger'
              })
            } else {
              this.$bvToast.toast(this.$t('sources.source_file_deactivation_error_description') + '(' + source.name + ').', {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('sources.source_file_deactivation_error'),
                variant: 'danger'
              })
            }
          }
        })
    },

    deleteSource (source) {
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/sources/delete/' + this.$route.params.id + '/' + source.id)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.sources.splice(this.sources.indexOf(source), 1)
            this.$attrs.project.sources.splice(this.$attrs.project.sources.findIndex(s => s.id === source.id), 1)
            this.$bvToast.toast(this.$t('sources.source_file_delete_description') + '(' + source.name + ').', {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('sources.source_file_delete'),
              variant: 'success'
            })
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$bvToast.toast(this.$t('sources.source_file_delete_error_description') + '(' + source.name + ').', {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('sources.source_file_delete_error'),
              variant: 'danger'
            })
          }
        })
    },

    previewSource (source) {
      const sourceAlert = this.getSourceAlert(source.id)
      if (sourceAlert.alertId !== undefined) {
        this.$bvToast.toast(this.$t('sources.source_preview_error_description_1') + sourceAlert.message + this.$t('sources.source_preview_error_description_2'), {
          autoHideDelay: 3000,
          solid: true,
          title: this.$t('sources.source_preview_error'),
          variant: 'danger'
        })
      } else {
        this.$router.push({ name: 'source.show', params: { projectId: this.$route.params.id, sourceId: source.id } })
      }
    },

    hasSourceAlerts (sourceId) {
      let result = false
      this.alerts.source.forEach(function (alert) {
        if (alert.sourceId === sourceId) result = true
      })
      return result
    },

    getSourceAlert (sourceId) {
      let sourceAlert = {}
      this.alerts.source.forEach(function (alert) {
        if (alert.sourceId === sourceId) sourceAlert = alert
      })
      return sourceAlert
    }
  },

  computed: {
    activeSources () {
      return this.sources.filter(source => source.active === true)
    },

    inactiveSources () {
      return this.sources.filter(source => source.active === false)
    },

    limitExceeded () {
      const sourcesCount = this.sources.filter(source => source.active === true).length
      if ((this.planLevel === 'basic') && (sourcesCount > 1)) {
        return true
      } else if ((this.planLevel === 'pro') && (sourcesCount > 3)) {
        return true
      } else return false
    }
  },

  components: { DashboardTemplate, PlusCircleIcon, SourceSummary }
}
</script>
